import { flexRender, type RowData } from '@tanstack/react-table';
import { type LegacyRow as Row } from '@tanstack/react-table/legacy';
import { Fragment, useMemo } from 'react';
import { type ExpandableData } from '../../types';
import { TableRow, TableCell, TableCellExpanded } from './style';
import { useTableRowVirtualizer } from './useTableRowVirtualizer';

type TableRowsProps<TData extends RowData> = {
  rows: Row<TData>[];
  infiniteScroll?: boolean;
  expandable?: boolean;
  expandableRowClick?: boolean;
  isLoadingMore: boolean;
  tableContainer: HTMLDivElement | null;
  loadMore?: () => void;
  hasMoreToLoad: boolean;
  verticalAlign?: 'top' | 'middle' | 'bottom';
};

export const TableRows = <TData extends RowData>({
  rows,
  infiniteScroll = false,
  tableContainer,
  isLoadingMore,
  loadMore = () => null,
  hasMoreToLoad,
  expandable,
  expandableRowClick,
  verticalAlign = 'middle',
}: TableRowsProps<TData>) => {
  const { virtualRows, rowVirtualizer, paddingBottom, paddingTop } = useTableRowVirtualizer({
    rows,
    isLoadingMore,
    tableContainer,
    loadMore,
    hasMoreToLoad,
    infiniteScroll,
  });

  const rowsToRender = useMemo(
    () =>
      infiniteScroll
        ? virtualRows.map((virtualRow) => ({
            row: rows[virtualRow.index],
            virtualRow,
          }))
        : rows.map((row) => ({ row, virtualRow: undefined })),
    [infiniteScroll, virtualRows, rows],
  );

  const numberOfColumns = rows[0]?.getVisibleCells().length ?? 1;

  return (
    <>
      {/* This row is needed to fill the space at the top of the table when using virtualization to
          ensure that useVirtualizer calculates the correct total size */}
      {infiniteScroll && paddingTop > 0 && (
        <TableRow aria-hidden>
          <TableCell
            verticalAlign={verticalAlign}
            colSpan={numberOfColumns}
            style={{ height: `${paddingTop}px`, padding: 0, border: 0 }}
          />
        </TableRow>
      )}
      {rowsToRender.map(({ row, virtualRow }) => {
        return (
          <Fragment key={row.id}>
            <TableRow
              data-index={virtualRow?.index}
              ref={(node) => {
                if (infiniteScroll && node) {
                  rowVirtualizer.measureElement(node);
                }
              }}
              data-testid={`table-row`}
              clickable={expandable && expandableRowClick}
              onClick={
                expandable && expandableRowClick
                  ? () => {
                      row.toggleExpanded();
                    }
                  : undefined
              }
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <TableCell verticalAlign={verticalAlign} key={cell.id} data-testid={`table-cell`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
            {row.getIsExpanded() && isExpandable(row.original) && (
              <TableRow key={`${row.id}-expanded`}>
                <TableCellExpanded
                  verticalAlign="middle"
                  colSpan={row.getVisibleCells().length}
                  data-testid={`table-cell-expanded-${row.id}`}
                >
                  {flexRender(row.original.expandedContent, {})}
                </TableCellExpanded>
              </TableRow>
            )}
          </Fragment>
        );
      })}
      {/* This row is needed to fill the space at the end of the table when using virtualization to
          ensure that useVirtualizer calculates the correct total size */}
      {infiniteScroll && paddingBottom > 0 && (
        <TableRow aria-hidden>
          <TableCell
            verticalAlign={verticalAlign}
            colSpan={numberOfColumns}
            style={{ height: `${paddingBottom}px`, padding: 0, border: 0 }}
          />
        </TableRow>
      )}
    </>
  );
};

const isExpandable = <TData,>(row: TData): row is TData & ExpandableData => {
  if (typeof row === 'object' && row !== null && 'expandedContent' in row) {
    return true;
  }

  return false;
};
